import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './Modal';
import { getCurrencyConfig } from '../utils/currency';
import {
  HISTORY_CURRENCIES,
  DATE_EXAMPLES,
  parseDateInput,
  formatLongDate,
  lookupHistoricalRate
} from '../utils/historicalRates';

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const weekdayOf = (iso) => {
  const [year, month, day] = iso.split('-').map(Number);
  return WEEKDAY_NAMES[new Date(year, month - 1, day).getDay()];
};

// Looks up what a currency was worth on a past date.
export const HistoricalRateModal = ({ isOpen, onClose }) => {
  const [amount, setAmount] = useState('1');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('IDR');
  const [dateText, setDateText] = useState('');
  const [amountError, setAmountError] = useState('');
  const [pairError, setPairError] = useState('');
  const [dateError, setDateError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  // An answer is only shown if it is for the latest lookup
  const lookupRef = useRef(0);
  const answerRef = useRef(null);

  // Start from the dashboard's display currency each time the dialog opens
  useEffect(() => {
    if (!isOpen) return;
    const { code } = getCurrencyConfig();
    setAmount('1');
    setFrom('USD');
    setTo(code === 'USD' ? 'IDR' : code);
    setDateText('');
    setAmountError('');
    setPairError('');
    setDateError('');
    setIsLoading(false);
    setAnswer(null);
    lookupRef.current += 1;
  }, [isOpen]);

  const parsedDate = dateText.trim() ? parseDateInput(dateText) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const numericAmount = amount.trim() === '' ? 1 : Number(amount);
    const isAmountValid = Number.isFinite(numericAmount) && numericAmount > 0;
    setAmountError(isAmountValid ? '' : 'Amount must be a number greater than 0.');

    const isPairValid = from !== to;
    setPairError(isPairValid ? '' : 'Choose two different currencies.');

    const resolved = parseDateInput(dateText);
    setDateError(resolved.error || '');

    if (!isAmountValid || !isPairValid || resolved.error) {
      setAnswer(null);
      return;
    }

    lookupRef.current += 1;
    const lookupId = lookupRef.current;
    setIsLoading(true);
    setAnswer(null);
    const result = await lookupHistoricalRate({ from, to, amount: numericAmount, date: resolved.date });
    if (lookupRef.current !== lookupId) return;
    setAnswer(result);
    setIsLoading(false);
  };

  const swapCurrencies = () => {
    setFrom(to);
    setTo(from);
  };

  // On a small screen the answer lands below the form
  useEffect(() => {
    const node = answerRef.current;
    if (answer && node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [answer]);

  let dateHint = `Try ${DATE_EXAMPLES}.`;
  if (parsedDate && parsedDate.date) dateHint = `${weekdayOf(parsedDate.date)}, ${formatLongDate(parsedDate.date)}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Historical exchange rate"
      footer={
        <button type="button" className="btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      <form className="history-form" onSubmit={handleSubmit} noValidate>
        <p className="settings-description">
          See what a currency was worth on a past date, back to January 1999.
        </p>

        <div className="history-fields">
          <div className="form-group">
            <label htmlFor="historyAmount">Amount</label>
            <input
              id="historyAmount"
              type="number"
              min="0"
              step="any"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="1"
              aria-invalid={amountError ? 'true' : undefined}
              aria-describedby={amountError ? 'history-amount-error' : undefined}
            />
          </div>
          <div className="form-group">
            <label htmlFor="historyFrom">From</label>
            <select id="historyFrom" value={from} onChange={(e) => { setFrom(e.target.value); setPairError(''); }}>
              {HISTORY_CURRENCIES.map((option) => (
                <option key={option.code} value={option.code}>{option.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn-secondary history-swap"
            onClick={swapCurrencies}
            aria-label="Swap currencies"
            title="Swap currencies"
          >
            ⇄
          </button>
          <div className="form-group">
            <label htmlFor="historyTo">To</label>
            <select
              id="historyTo"
              value={to}
              onChange={(e) => { setTo(e.target.value); setPairError(''); }}
              aria-invalid={pairError ? 'true' : undefined}
              aria-describedby={pairError ? 'history-pair-error' : undefined}
            >
              {HISTORY_CURRENCIES.map((option) => (
                <option key={option.code} value={option.code}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
        {amountError && <span id="history-amount-error" className="field-error" role="alert">{amountError}</span>}
        {pairError && <span id="history-pair-error" className="field-error" role="alert">{pairError}</span>}

        <div className="form-group history-date">
          <label htmlFor="historyDate">Date</label>
          <input
            id="historyDate"
            type="text"
            value={dateText}
            onChange={(e) => { setDateText(e.target.value); setDateError(''); }}
            placeholder="yesterday, last Monday, 17 Sep 2025, 2025-09-17"
            autoComplete="off"
            data-autofocus=""
            aria-invalid={dateError ? 'true' : undefined}
            aria-describedby={dateError ? 'history-date-error' : 'history-date-hint'}
          />
          {dateError ? (
            <span id="history-date-error" className="field-error" role="alert">{dateError}</span>
          ) : (
            <span id="history-date-hint" className="form-text">{dateHint}</span>
          )}
        </div>

        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Looking up…' : 'Look up rate'}
        </button>
      </form>

      {answer && (
        <div
          ref={answerRef}
          className={`history-answer history-answer-${answer.tone}`}
          role={answer.tone === 'error' ? 'alert' : 'status'}
        >
          {answer.notice && <p className="history-notice">{answer.notice}</p>}
          {answer.headline && <p className="history-headline">{answer.headline}</p>}
          {answer.detail && <p className="history-detail">{answer.detail}</p>}
        </div>
      )}
    </Modal>
  );
};
